import { useMemo, useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import useLocalStorage from "utils/useLocalStorage";



import { useEnvs } from "./EnvContext";
import { evalShouldFetchEnvs } from "./store";
import LoadingScreen from "components/LoadingScreen";
import SomethingWrong from "pages/FallbackPages/SomethingWrong";

// The purpose of EnvManager here is to manage the environment variables and fetch them from the server if they are not available in the local storage.
// Also, they handle cases where the user might manipulate the environment variables in the browser.
// The main store is `EnvStore` which is a zustand store that holds the environment variables and their version.
const EnvManager = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const hasFetchedBeforeRender = useRef(evalShouldFetchEnvs());

  const { fetchEnvs } = useEnvs();

  const [envs] = useLocalStorage("envs", {});
  const [version] = useLocalStorage("env_version", {});

  const isEnvEmpty = useMemo(
    () => isNaN(Number(version)) || !envs || Object.keys(envs).length === 0,
    [envs, version],
  );

  const { isLoading, isError, failureCount } = useQuery(["get-environment-variables"], fetchEnvs, {
    enabled: isEnvEmpty,
    retryDelay: 1000,
    refetchOnMount: false,
    onSuccess() {
      hasFetchedBeforeRender.current = false;
    },
    retry: (failureCount) => {
      if (failureCount > 3) {
        return false;
      }

      return true;
    },
    useErrorBoundary: false,
  });
  const isRenderBlocked =
    isLoading && isEnvEmpty && !hasFetchedBeforeRender.current;
  if (isRenderBlocked) {
    return <LoadingScreen className="bg-white" />;
  }

  if (isError && failureCount >= 2) {
    return <SomethingWrong/>;
  }
  
  return <>{children}</>;
};

export { EnvManager };
